import { toFunctionSelector, toEventSelector, parseAbi, decodeEventLog } from 'viem';

// src/abi/types.ts
function emptyNormalizedAbi() {
  return { entries: [], reads: [], writes: [], events: [], errors: [], selectorIndex: {}, topicIndex: {} };
}
function paramType(p) {
  if (p.type.startsWith("tuple")) {
    const components = p.components ?? [];
    const inner = components.map(paramType).join(",");
    const suffix = p.type.slice("tuple".length);
    return `(${inner})${suffix}`;
  }
  return p.type;
}
function functionSignature(fn) {
  return `${fn.name}(${(fn.inputs ?? []).map(paramType).join(",")})`;
}
function eventSignature(ev) {
  return `${ev.name}(${(ev.inputs ?? []).map(paramType).join(",")})`;
}
function functionSelector(fn) {
  return toFunctionSelector(functionSignature(fn));
}
function eventTopic(ev) {
  return toEventSelector(eventSignature(ev));
}
var byName = (a, b) => (a.name ?? "").localeCompare(b.name ?? "");
function normalizeAbi(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return emptyNormalizedAbi();
  const reads = [];
  const writes = [];
  const events = [];
  const errors = [];
  const selectorIndex = {};
  const topicIndex = {};
  for (const item of entries) {
    if (item.type === "function") {
      const fn = item;
      if (fn.stateMutability === "view" || fn.stateMutability === "pure") reads.push(fn);
      else writes.push(fn);
      try {
        selectorIndex[functionSelector(fn)] = fn;
      } catch {
      }
    } else if (item.type === "event") {
      const ev = item;
      events.push(ev);
      try {
        topicIndex[eventTopic(ev)] = ev;
      } catch {
      }
    } else if (item.type === "error") {
      errors.push(item);
    }
  }
  reads.sort(byName);
  writes.sort(byName);
  events.sort(byName);
  errors.sort(byName);
  return { entries, reads, writes, events, errors, selectorIndex, topicIndex };
}
function parseAbiJson(text) {
  const trimmed = text.trim();
  if (!trimmed) return { abi: null, error: new Error("ABI is empty.") };
  try {
    const parsed = JSON.parse(trimmed);
    const entries = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.abi) ? parsed.abi : null;
    if (!entries) return { abi: null, error: new Error("ABI must be a JSON array of items.") };
    return { abi: normalizeAbi(entries), error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function parseHumanAbi(signatures) {
  const lines = signatures.map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) return { abi: null, error: new Error("No signatures provided.") };
  try {
    const entries = parseAbi(lines);
    return { abi: normalizeAbi(entries), error: null };
  } catch (err) {
    return { abi: null, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
function abiMetaFromDto(dto) {
  return {
    chainId: dto.chain_id,
    address: dto.address,
    contractName: dto.contract_name ?? null,
    compilerVersion: dto.compiler_version ?? null,
    isProxy: dto.is_proxy,
    implementationAddresses: dto.implementation_addresses,
    source: dto.source ?? null,
    fetchedFrom: dto.fetched_from ?? null,
    confidence: dto.confidence,
    lifecycle: typeof dto.lifecycle === "string" ? dto.lifecycle : void 0,
    isHumanApproved: dto.is_human_approved,
    orgId: dto.org_id,
    tenantId: dto.tenant_id,
    updatedAt: dto.updated_at ?? null
  };
}
function normalizedAbiFromDto(dto) {
  return normalizeAbi(dto.entries ?? []);
}
function abiToJson(abi) {
  return JSON.stringify(abi.entries, null, 2);
}

// src/abi/merge.ts
function itemKey(item) {
  if (item.type === "function") return `function:${functionSignature(item)}`;
  if (item.type === "event") return `event:${eventSignature(item)}`;
  if (item.type === "error") {
    const e = item;
    return `error:${e.name}(${(e.inputs ?? []).map((i) => i.type).join(",")})`;
  }
  return `${item.type}`;
}
function mergeNormalizedAbis(abis) {
  const seen = /* @__PURE__ */ new Set();
  const entries = [];
  for (const n of abis) {
    for (const item of n.entries) {
      const key = itemKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(item);
    }
  }
  return normalizeAbi(entries);
}
function signatureSet(abi) {
  const out = /* @__PURE__ */ new Set();
  for (const fn of [...abi.reads, ...abi.writes]) out.add(`function:${functionSignature(fn)}`);
  for (const ev of abi.events) out.add(`event:${eventSignature(ev)}`);
  return out;
}
function diffAbis(a, b) {
  const sa = signatureSet(a);
  const sb = signatureSet(b);
  const onlyA = [...sa].filter((s) => !sb.has(s)).sort();
  const onlyB = [...sb].filter((s) => !sa.has(s)).sort();
  const common = [...sa].filter((s) => sb.has(s)).sort();
  return { onlyA, onlyB, common };
}

// src/abi/defaults.ts
var ERC20_SIGNATURES = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)"
];
var ERC721_EXTRA_SIGNATURES = [
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",
  "function setApprovalForAll(address operator, bool approved)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)"
];
var DEFAULT_ERC_SIGNATURES = [...ERC20_SIGNATURES, ...ERC721_EXTRA_SIGNATURES];
var cached = null;
function defaultErcAbi() {
  if (cached) return cached;
  const { abi } = parseHumanAbi(DEFAULT_ERC_SIGNATURES);
  cached = abi ?? emptyNormalizedAbi();
  return cached;
}
function coerce(abi) {
  return "topicIndex" in abi ? abi : normalizeAbi(abi);
}
function decodeLog(log, abi) {
  const normalized = coerce(abi);
  const topic0 = log.topics[0];
  const base = {
    address: log.address,
    topic0,
    blockNumber: log.blockNumber ?? null,
    logIndex: log.logIndex ?? null,
    transactionHash: log.transactionHash ?? null,
    decoded: false
  };
  if (!topic0) return { ...base, reason: "anonymous event (no topic0)" };
  const known = normalized.topicIndex[topic0];
  try {
    const result = decodeEventLog({
      abi: normalized.entries,
      data: log.data,
      topics: log.topics
    });
    return {
      ...base,
      eventName: result.eventName,
      args: result.args ?? void 0,
      decoded: true
    };
  } catch (error) {
    return {
      ...base,
      eventName: known?.name,
      reason: known ? errorMessage(error) : "unknown event (topic0 not in ABI)"
    };
  }
}
function decodeLogs(logs, abi) {
  const normalized = coerce(abi);
  return logs.map((log) => decodeLog(log, normalized));
}
function summarizeLogs(logs) {
  const counts = /* @__PURE__ */ new Map();
  for (const log of logs) {
    const name = log.eventName ?? "(unknown)";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
function errorMessage(error) {
  if (error instanceof Error) return error.message.split("\n")[0] ?? "decode failed";
  return "decode failed";
}

export { DEFAULT_ERC_SIGNATURES, abiMetaFromDto, abiToJson, decodeLog, decodeLogs, defaultErcAbi, diffAbis, emptyNormalizedAbi, eventSignature, eventTopic, functionSelector, functionSignature, itemKey, mergeNormalizedAbis, normalizeAbi, normalizedAbiFromDto, parseAbiJson, parseHumanAbi, signatureSet, summarizeLogs };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map